import React, { useEffect, useState } from 'react';
import { Form, Input, Select, Button, DatePicker, Card, message, Space, Divider, Typography, Row, Col } from 'antd';
import { fetchPackageAdminList } from '@/api/Admin/userPackage';
import { generateSigningContractUrl, SigningContractPayload } from '@/lib/signingContractPayload';
import { ArrowLeftOutlined, CopyOutlined, LinkOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { CRM_HOST } from '@/env';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;
const { Option } = Select;

interface ContractPackage {
    id: number;
    name: string;
    price: string;
    type?: string;
    parent?: {
        id: number;
        name: string;
    };
}

const CreateContractData: React.FC = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [packages, setPackages] = useState<ContractPackage[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);

    useEffect(() => {
        const loadPackages = async () => {
            setLoading(true);
            try {
                const response = await fetchPackageAdminList();
                if (response.status && response.data) {
                    setPackages(response.data.data);
                }
            } catch (error) {
                console.error('Failed to fetch packages:', error);
            } finally {
                setLoading(false);
            }
        };
        loadPackages();
    }, []);

    const onFinish = async (values: any) => {
        setSubmitting(true);
        try {
            const selectedPackage = packages.find(p => p.id === values.packageId);
            const contractName =
                selectedPackage?.type === 'mini' && selectedPackage.parent?.name
                    ? selectedPackage.parent.name
                    : selectedPackage?.name;
            
            const payload: SigningContractPayload = {
                packageId: values.packageId,
                contractInfo: {
                    name: contractName,
                    price: Number(selectedPackage?.price),
                },
                contractData: {
                    contractNumber: values.contractNumber,
                    signDate: values.signDate?.format('YYYY-MM-DD'),
                    depositDate: values.depositDate?.format('YYYY-MM-DD'),
                    finalPaymentDate: values.finalPaymentDate?.format('YYYY-MM-DD'),
                    investor: {
                        fullName: values.fullName,
                        idNumber: values.idNumber,
                        dob: values.dob?.format('YYYY-MM-DD'),
                        issueDate: values.issueDate?.format('YYYY-MM-DD'),
                        issuePlace: values.issuePlace,
                        email: values.email,
                        phone: values.phone,
                        address: values.address,
                        bankAccount: values.bankAccount,
                        bank: values.bank,
                    },
                },
            };

            const url = await generateSigningContractUrl(payload);
            // In a real application, the base URL might need to be prefixed with the current domain
            const fullUrl = `${CRM_HOST}${url}`;
            setGeneratedUrl(fullUrl);
            message.success(t('createContractData.messages.generateSuccess'));
        } catch (error) {
            console.error('Failed to generate URL:', error);
            message.error(t('createContractData.messages.generateFailed'));
        } finally {
            setSubmitting(false);
        }
    };

    const copyToClipboard = () => {
        if (generatedUrl) {
            navigator.clipboard.writeText(generatedUrl);
            message.success(t('createContractData.messages.copySuccess'));
        }
    };

    return (
        <div style={{ padding: '24px' }}>
            <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate('/admin/plans')}
                style={{ marginBottom: '16px' }}
            >
                {t('createContractData.backToPlans')}
            </Button>
            
            <Card title={<Title level={3}>{t('createContractData.title')}</Title>} bordered={false}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{
                        signDate: dayjs(),
                    }}
                >
                    <Divider orientation="left">{t('createContractData.sections.planInfo')}</Divider>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="packageId"
                                label={t('createContractData.form.selectPlan')}
                                rules={[{ required: true, message: t('createContractData.validation.selectPlan') }]}
                            >
                                <Select loading={loading} placeholder={t('createContractData.form.selectPlan')}>
                                    {packages.map(pkg => (
                                        <Option key={pkg.id} value={pkg.id}>
                                            {pkg.name} - {Number(pkg.price).toLocaleString()} {t('createContractData.form.vnd')}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="contractNumber"
                                label={t('createContractData.form.contractNumber')}
                            >
                                <Input placeholder={t('createContractData.form.contractNumberPlaceholder')} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider orientation="left">{t('createContractData.sections.investorInfo')}</Divider>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="fullName"
                                label={t('createContractData.form.fullName')}
                                rules={[{ required: true, message: t('createContractData.validation.required') }]}
                            >
                                <Input placeholder="NGUYEN VAN A" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="idNumber"
                                label={t('createContractData.form.idNumber')}
                                rules={[{ required: true, message: t('createContractData.validation.required') }]}
                            >
                                <Input placeholder="079123456789" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="dob" label={t('createContractData.form.dob')}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="issueDate" label={t('createContractData.form.issueDate')}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="issuePlace" label={t('createContractData.form.issuePlace')}>
                                <Input placeholder={t('createContractData.form.issuePlacePlaceholder')} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="email" label={t('createContractData.form.email')} rules={[{ type: 'email' }]}> 
                                <Input placeholder={t('createContractData.form.emailPlaceholder')} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="phone" label={t('createContractData.form.phone')}>
                                <Input placeholder="0909123456" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="address" label={t('createContractData.form.address')}>
                        <Input placeholder={t('createContractData.form.addressPlaceholder')} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="bank" label={t('createContractData.form.bankName')}>
                                <Input placeholder="Vietcombank" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="bankAccount" label={t('createContractData.form.bankAccount')}>
                                <Input placeholder="0123456789" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider orientation="left">{t('createContractData.sections.contractDates')}</Divider>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="signDate" label={t('createContractData.form.signDate')}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="depositDate" label={t('createContractData.form.depositDate')}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="finalPaymentDate" label={t('createContractData.form.finalPaymentDate')}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={submitting} icon={<SaveOutlined />}>
                                {t('createContractData.actions.generateUrl')}
                            </Button>
                            <Button onClick={() => form.resetFields()}>
                                {t('createContractData.actions.reset')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>

                {generatedUrl && (
                    <div style={{ marginTop: '24px', padding: '16px', background: '#f5f5f5', borderRadius: '8px' }}>
                        <Title level={4}>{t('createContractData.generated.title')}</Title>
                        <Text copyable={{ text: generatedUrl }}>{generatedUrl}</Text>
                        <div style={{ marginTop: '16px' }}>
                            <Space>
                                <Button icon={<CopyOutlined />} onClick={copyToClipboard}>
                                    {t('createContractData.actions.copyLink')}
                                </Button>
                                <Button type="link" icon={<LinkOutlined />} href={generatedUrl} target="_blank">
                                    {t('createContractData.actions.openLink')}
                                </Button>
                            </Space>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default CreateContractData;
